// import EventEmitter from 'eventemitter3';
import { createWorkerManager } from './worker-manager.js';
import { createStatusManager } from './status-manager.js';
import { createInitialSessionData, processAttemptResults } from './utils/measurement-processing.js';
import EventEmitter from './utils/buffered-eventemitter';

let videoFrameCallback;
let videoEl;
let countdownSetIntervalId;
let currentTimeLeft;
let vitalsHaveBeenAuthorized;
let authorizedVitals;
// Derived from the authorization response; lives with the other module-level
// auth state so a second client instance sees a consistent view.
let maxSessionTime = 0;
let inPreviewMode = false;
let guideCoordinatesSet = false;
let currentAttemptSignsMsgs = {}; // Collect signsMessage events during session
let captureTimeUsed = false;
let stopFuncCalled = false;

let remainingSessionAttempts = 3;
let vitalsSessionData = {};
let finalVitalsMeasurementValues = {};
let wasmResultsAllAttempts = []; // Full endSession message per attempt, newest first
let signsMessagesAllAttempts = [];
let metadataAllAttempts = [];

// Lost-face (E-FDM-041) restart state.
let restarting = false;           // true while a restart is in flight; frames are ignored
let faceLostRestartCount = 0;     // consecutive restarts since the face was last valid
let currentSelectedVitals = null; // remembered so a restart can report the vitals in play
let currentSessionTags = null;    // remembered so a restart can re-tag the new core session

let imageWidth;
let imageHeight;
let imageXOffset;
let imageYOffset;
let rawVideoDimensions;
let coreVideoDimensions;

// Critical (C-*) status codes the core can emit. The core never ends a session
// on these itself (criticalError() only suppresses the S-GEN progress codes and
// the C-code re-emits every frame), so they are surfaced via the criticalError
// event for the app to act on. Any unmapped C-* code is still treated as
// critical by prefix, since the first character of a status code is its
// severity class.
const criticalStatusMessageMap = {
  'C-GEN-005': 'Internal error: required input missing or invalid image size',
  'C-VPU-051': 'Internal error: no frame data received',
  'C-BPR-093': 'Blood pressure prediction failed',
  'C-DQM-023': 'Camera frame rate too low for measurement',
  'C-DQM-024': 'Camera frame rate too unstable for measurement',
  'C-FDM-043': 'Face detection model unavailable',
};

const statusWarningMessageMap = {
  'E-DQM-021': { message: 'Please stay still', code: 'E-DQM-021', severity: 'warning' },
  'E-DQM-022': { message: 'Improve your lighting so your face is evenly lit', code: 'E-DQM-022', severity: 'warning' },
  'E-FDM-041': { message: 'Face not detected', code: 'E-FDM-041', severity: 'warning' },
  'E-FDM-042': { message: 'Please keep face centered', code: 'E-FDM-042', severity: 'warning' },
  'W-RES-073': { message: 'Please keep face centered', code: 'W-RES-073', severity: 'warning' },
  'W-RES-076': { message: 'Please keep face centered', code: 'W-RES-076', severity: 'warning' },
  'W-RES-078': { message: 'Please keep face centered', code: 'W-RES-078', severity: 'warning' },
  'W-RES-079': { message: 'Face too far from camera', code: 'W-RES-079', severity: 'warning' },
  'W-RES-080': { message: 'Face too close to camera', code: 'W-RES-080', severity: 'warning' },
};

const processDataStatus = (dataStatusArr) => {
  return dataStatusArr.map((statusCode) => {
    if (statusWarningMessageMap[statusCode]) {
      return statusWarningMessageMap[statusCode];
    }

    // The first character of a status code is its severity class (C/E/W/S/I)
    if (statusCode.startsWith('C-')) {
      return { code: statusCode, message: criticalStatusMessageMap[statusCode] || 'Critical error', severity: 'critical' };
    }

    return { code: statusCode, message: '', severity: 'info' };
  });
}

/**
 * Creates a Vital Client - Primary SDK interface
 * @param {Object} config - Configuration object
 * @param {string} config.workerDirectory - Path to worker directory
 * @param {Function} config.authenticator - Callback function(runToken) that returns auth response
 */
export function createVitalClient(config = {}) {
  const emitter = new EventEmitter();
  const clientConfig = {
    ...config,
  };

  let workerManager = null;
  const statusManager = createStatusManager();
  let initialized = false;
  let startTimestamp;
  let coreVersion = null; // Vital Core (WASM) version string, cached once the worker is ready

  // Max consecutive lost-face restarts before giving up and ending the attempt.
  // The core emits E-FDM-041 once the face has been continuously undetected past
  // its ~1s threshold; we restart the attempt each time, up to this many times in
  // a row without the face being re-acquired, then end the attempt so the app can
  // surface "couldn't detect a face." Configurable via config.maxFaceLostRestarts.
  const maxFaceLostRestarts = clientConfig.maxFaceLostRestarts ?? 5;

  // Frame counting
  let framesSent = 0;
  let framesProcessed = 0;
  let bytesSent = 0;
  let frameMode = 'idle';
  let sessionCount = 0;
  let bitmapCreateMs = 0;
  let bitmapCreateMax = 0;
  let bitmapCreateSum = 0;

  /**
   * Initialize the SDK and worker
   */
  const init = async () => {
    if (initialized) {
      throw new Error('VitalClient already initialized');
    }

    workerManager = createWorkerManager(clientConfig);

    // Forward worker events
    workerManager.on('ready', () => {
      initialized = true;
      statusManager.updateStatus({ initialized: true });
      emitter.emit('ready');
      // Cache the core version once, so it can be stamped into every stop
      // result without an extra worker round-trip. Best-effort: a failure here
      // must not affect readiness, so it is not awaited and errors are ignored.
      workerManager.callMethod('getVersionString')
        .then((version) => { coreVersion = version ?? null; })
        .catch(() => {});
    });

    workerManager.on('error', (error) => {
      emitter.emit('error', error);
    });

    workerManager.on('log', (msg) => {
      emitter.emit('log', msg);
    });

    workerManager.on('signsMessage', (msg) => {
      framesProcessed++;
      // Worker-side frame failures arrive as { error: true, errorMessage, ... }
      // with no data payload. Surface them as an error event rather than
      // letting handleSignsMsgs throw on a missing message.
      if (msg.error || !msg.data) {
        const error = new Error(msg.errorMessage || 'Worker frame processing error');
        error.frameTimestamp = msg.timestamp;
        error.stack = msg.errorStack || error.stack;
        emitter.emit('error', error);
        return;
      }
      handleSignsMsgs(msg.data, msg.duration, msg.totalDuration, msg.imageDataDuration);
    });

    workerManager.on('segmentationDuration', (data) => {
      emitter.emit('segmentationDuration', data);
    });

    workerManager.on('faceDetectionDuration', (data) => {
      emitter.emit('faceDetectionDuration', data);
    });

    try {
      await workerManager.init();
    } catch (e) {
      workerManager.destroy();
      throw e;
    }
  };

  /**
   * Processes message from the vital core algorithm to determine when to end
   * a session attempt
   */
  const handleSignsMsgs = async (msg, duration, totalDuration, imageDataDuration) => {
    // While a lost-face restart is in flight the old core session is being torn
    // down and a fresh one started. Ignore incoming frames until it completes so
    // they can't race the reset or double-trigger another restart.
    if (restarting) {
      return;
    }

    const { vitals } = msg;

    // Store message with timestamp (matches vital-trac-app pattern). Attach the
    // worker's per-frame timing (WASM processFrame duration, total pipeline
    // duration, ImageData conversion duration) so submissions carry processing
    // times without the app needing an activity logger. dataStatus stays the
    // raw string array; only the emitted clone below gets the processed form.
    const currTimestamp = Date.now();
    currentAttemptSignsMsgs[currTimestamp] = { ...msg, duration, totalDuration, imageDataDuration };

    const msgClone = { ...msg, duration, totalDuration, imageDataDuration };
    // Guard against a missing dataStatus: the core can send a sparse signs
    // message, and consumers iterate msgClone.dataStatus, so always emit an array.
    msgClone.dataStatus = processDataStatus(msg.dataStatus || []);

    // Surface critical (C-*) codes. The core never ends the session on these
    // itself and neither does the SDK; the app decides how to react.
    for (const status of msgClone.dataStatus) {
      if (status.severity === 'critical') {
        emitter.emit('criticalError', status);
      }
    }

    emitter.emit('signsMessage', msgClone);

    // Lost face: the core emits E-FDM-041 on every frame once the face has been
    // continuously undetected past its threshold (~1s by default). Rather than
    // let the attempt grind on producing no/low-confidence data, restart the
    // core session so measurement starts clean when the face returns. Only
    // during an active measurement: preview mode has no session to restart.
    if (startTimestamp && msg.dataStatus && msg.dataStatus.includes('E-FDM-041')) {
      await handleFaceLost();
      return;
    }

    // Face is present and tracked this frame: clear the consecutive-restart
    // budget so a brief, recovered loss earlier in the attempt doesn't count
    // against a later, independent one. FaceTracker.valid stays false during the
    // sub-threshold window right after a restart, so a permanently-absent face
    // never resets here and will reach the give-up cap.
    if (msg.metadata?.FaceTracker?.valid === true) {
      faceLostRestartCount = 0;
    }

    if (
      startTimestamp
      && (allEnabledVitalsComplete(vitals) || (Date.now() - startTimestamp > (maxSessionTime * 1000)))
     ) {
      await stop();
    }
  };

  /**
   * Handle a lost-face (E-FDM-041) frame. Restart the current attempt so a
   * transient loss doesn't poison the measurement, up to maxFaceLostRestarts
   * consecutive times. Past that, give up: end the attempt flagged invalid so
   * the app can surface "couldn't detect a face" rather than restart forever on
   * an empty frame. A restart happens WITHIN an attempt and never consumes one
   * of the session's retry attempts; the give-up stop ends the attempt normally
   * (consuming it), leaving any remaining retry attempts available.
   */
  const handleFaceLost = async () => {
    // Set synchronously before any await so the per-frame E-FDM-041 storm and
    // any frames arriving mid-restart are ignored by the guard at the top of
    // handleSignsMsgs (one lost-face episode => one restart, not one per frame).
    restarting = true;
    faceLostRestartCount += 1;

    if (faceLostRestartCount > maxFaceLostRestarts) {
      emitter.emit('faceLostTimeout', {
        code: 'E-FDM-041',
        restarts: faceLostRestartCount - 1,
        message: 'Face not detected; ending attempt after repeated restarts',
      });
      // Terminal: end the attempt and flag it. stop() cancels frame ingestion,
      // so no further signs messages arrive; clearing `restarting` is tidiness.
      await stop({ noValidMeasurements: true, endReason: 'face-lost' });
      restarting = false;
      return;
    }

    emitter.emit('faceLost', {
      code: 'E-FDM-041',
      restart: faceLostRestartCount,
      maxRestarts: maxFaceLostRestarts,
      message: 'Face not detected; restarting measurement',
    });

    await restartAttempt();
    restarting = false;
  };

  /**
   * Restart the core measurement session in place, keeping the same start()
   * lifecycle: end the current core session (discarding its partial result),
   * reset the per-attempt signs accumulator, and start a fresh core session
   * with the same session tags. The video frame ingestion loop is left running
   * and no 'stop' result is produced, so from the consumer's perspective this
   * is one continuous attempt whose clock simply restarts. It is NOT a retry
   * attempt and does not touch the remainingSessionAttempts budget.
   */
  const restartAttempt = async () => {
    // Discard the failed session's partial data.
    await workerManager.callMethod('endSession');

    currentAttemptSignsMsgs = {};

    await workerManager.callMethod('startSession', currentSessionTags);

    // Restart the session clock; clear the old interval first so intervals don't stack.
    clearInterval(countdownSetIntervalId);
    startCountdownLoop();
    startTimestamp = Date.now();

    emitter.emit('restart', { selectedVitals: currentSelectedVitals, restart: faceLostRestartCount });
  };

  const setImageDimensions = (videoEl, zoomedIn) => {
    if (videoEl.videoWidth > videoEl.videoHeight) {
      imageWidth = 640;
      imageHeight = 480;
    } else {
      imageWidth = 480;
      imageHeight = 640;
    }

    if (zoomedIn) {
      imageXOffset = Math.round((videoEl.videoWidth - imageWidth) / 2);
      imageYOffset = Math.round((videoEl.videoHeight - imageHeight) / 2);
    } else {
      imageXOffset = 0;
      imageYOffset = 0;
    }

    coreVideoDimensions = `${imageWidth}x${imageHeight}`;
    rawVideoDimensions = `${videoEl.videoWidth}x${videoEl.videoHeight}`;
  }

  /**
   * Ported over from vital-trac. Returns size and positioning of guide. These values are used
   * to instruct vital core where patients should be located in the images passed to vital core
   * @param {HTMLVideoElement} videoEl - html video element used to determin video orientation
   * @returns {Object} coordinates of guide
   */
  const getGuideCoordinates = (videoEl) => {
    // Percentages (0-1)
    let videoWidth = 480;
    let videoHeight = 640;

    if (videoEl.videoWidth > videoEl.videoHeight) {
      videoWidth = 640;
      videoHeight = 480;
    }

    const wPct = 0.5; // Width of the box is half the width of canvas
    const hPct = 0.5; // Height of the box is half the height of canvas
    const xPct = 0.0; // 0.0 is center, -0.1 is left of center
    const yPct = -0.1; // 0.0 is center, -0.1 is above center
    const width = Math.min(videoWidth, videoHeight) * wPct;
    const height = Math.max(videoWidth, videoHeight) * hPct;
    const x = videoWidth / 2 + xPct * videoWidth - width / 2;
    const y = videoHeight / 2 + yPct * videoHeight - height / 2;

    return { x, y, width, height };
  }

  /**
   * [Start] measurement session
   */
  const start = async (sessionConfig, videoEl, zoomedIn) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized. Call init() first.');
    }
    if (!vitalsHaveBeenAuthorized) {
      throw new Error('Vital Core Algorithm has not been authorized');
    }
    if (!videoEl || !(videoEl instanceof HTMLVideoElement)) {
      throw new Error('A video element is required by the start() function')
    }
    if (inPreviewMode) {
      await stopPreviewMode();
    }

    setImageDimensions(videoEl, zoomedIn);

    stopFuncCalled = false;

    if (!guideCoordinatesSet) {
      const guideCoordinates = getGuideCoordinates(videoEl);
      await workerManager.callMethod('setFaceGuide', guideCoordinates);
      emitter.emit('guideCoordinates', guideCoordinates);
      guideCoordinatesSet = true;
    }

    let selectedVitals;

    if (remainingSessionAttempts === 0) {
      throw new Error('No remaining session attempts left');
    } else if (remainingSessionAttempts === 3) {
      selectedVitals = sessionConfig.vitals;
      vitalsSessionData = createInitialSessionData(sessionConfig.vitals);
    } else {
      selectedVitals = Object.entries(vitalsSessionData).reduce((vitalsToMeasure, [vital, vitalData]) => {
        if (vitalData.needToMeasure) {
          vitalsToMeasure.push(vital);
        }
        return vitalsToMeasure;
      }, []);
    }

    // Reset frame counters and signs messages collection for new session
    framesSent = 0;
    framesProcessed = 0;
    bytesSent = 0;
    bitmapCreateMs = 0;
    bitmapCreateMax = 0;
    frameMode = 'session';
    sessionCount++;
    currentAttemptSignsMsgs = {};

    // Reset lost-face restart state for the new attempt and remember the
    // session tags so a mid-attempt restart can re-tag the fresh core session.
    restarting = false;
    faceLostRestartCount = 0;
    currentSelectedVitals = selectedVitals;

    const sessionTags = JSON.stringify(sessionConfig);
    currentSessionTags = sessionTags;
    await workerManager.callMethod('startSession', sessionTags);

    startCountdownLoop();
    startTimestamp = Date.now();
    statusManager.updateStatus({ measuring: true });
    emitter.emit('start', { selectedVitals });
    if (!videoFrameCallback) {
      ingestFrames(videoEl);
    }
  };

  /**
   * Start preview mode
   */
  const startPreviewMode = async (videoEl, zoomedIn = false) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }

    setImageDimensions(videoEl, zoomedIn);

    const guideCoordinates = getGuideCoordinates(videoEl);
    await workerManager.callMethod('setFaceGuide', guideCoordinates);
    emitter.emit('guideCoordinates', guideCoordinates);
    guideCoordinatesSet = true;

    try {
      await workerManager.callMethod('previewSession');
      inPreviewMode = true;
      framesSent = 0;
      framesProcessed = 0;
      bytesSent = 0;
      bitmapCreateMs = 0;
      bitmapCreateMax = 0;
      bitmapCreateSum = 0;
      frameMode = 'preview';
      ingestFrames(videoEl);
      emitter.emit('previewModeStart');
    } catch (e) {
      console.log('preview mode failed to start', e);
      throw new Error('Preview mode failed to start');
    }
  }

  /**
   * Stop preview mode
   */
  const stopPreviewMode = async () => {
    try {
      videoEl.cancelVideoFrameCallback(videoFrameCallback);
      videoFrameCallback = null;
      await workerManager.callMethod('endSession');
      inPreviewMode = false;
      emitter.emit('previewModeStop');
    } catch {
      throw new Error('Preview mode failed to stop');
    }
  }

  /**
   * Stop measurement session
   *
   * @param {Object} [options]
   * @param {boolean} [options.noValidMeasurements=false] - mark the result invalid
   *   (used by the lost-face give-up path so consumers can distinguish it).
   * @param {string|null} [options.endReason=null] - why the attempt ended
   *   (e.g. 'face-lost'); null for a normal early-end / max-time / manual stop.
   */
  const stop = async (options = {}) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }

    if (stopFuncCalled === true) return;

    stopFuncCalled = true;
    const timestamp = Date.now();
    videoEl.cancelVideoFrameCallback(videoFrameCallback);
    videoFrameCallback = null;

    let wasmResult = await workerManager.callMethod('endSession');
    wasmResult = JSON.parse(wasmResult);
    startTimestamp = null;

    // The core version is normally cached at worker-ready; if that fetch failed
    // or hasn't resolved yet, fetch it now so results always carry it.
    if (coreVersion == null) {
      try {
        coreVersion = (await workerManager.callMethod('getVersionString')) ?? null;
      } catch {
        // leave null; the field is still present in the result
      }
    }

    currentAttemptSignsMsgs[Date.now()] = wasmResult;

    // Format result to match vital-trac-app structure
    // This makes it ready for VITAL-TRAC PRO submission
    const vitalMeasurements = wasmResult.vitals || {};

    // Map BP results - supports both regressor (sbp_mmHg/dbp_mmHg) and classifier (hypertensive/confidence) formats
    for (const bpTag of ['BP_GW', 'BP_MD']) {
      const bp = vitalMeasurements[bpTag];
      if (!bp) continue;
      if (bp.sbp_mmHg && bp.dbp_mmHg) {
        bp.BP_SYS = bp.sbp_mmHg;
        bp.BP_DIA = bp.dbp_mmHg;
      } else if (bp.hypertensive !== undefined && bp.confidence !== undefined) {
        bp.classification = bp.hypertensive ? 'Hypertensive' : 'Normal';
      }
    }

    const prevFinalVitalsMeasurementValues = finalVitalsMeasurementValues || {};

    const processedAttemptResults = processAttemptResults(vitalMeasurements, vitalsSessionData, prevFinalVitalsMeasurementValues);
    const {
      sessionComplete,
      updatedVitalsData,
      updatedFinalValues,
    } = processedAttemptResults;

    vitalsSessionData = { ...vitalsSessionData, ...updatedVitalsData };
    finalVitalsMeasurementValues = updatedFinalValues;
    remainingSessionAttempts = sessionComplete ? 0 : remainingSessionAttempts - 1;
    wasmResultsAllAttempts = [wasmResult, ...wasmResultsAllAttempts];
    signsMessagesAllAttempts = [currentAttemptSignsMsgs, ...signsMessagesAllAttempts];
    metadataAllAttempts = [wasmResult.metadata, ...metadataAllAttempts];

    // CONTRACT (verified against vital-trac-app + the Mindset API):
    //   result.vitals = the FULL final endSession message from the core. That
    //   message carries `.vitals` (per-signal readings, so the API's
    //   `data.vitals.vitals` read path is unchanged) PLUS `.dataStatus` (incl.
    //   the "I-DBG-000-SessionEnd" marker), `.sessionTime`, `.metadata`, and
    //   `.processorTimes`. The API picks the per-signal values and archives the
    //   rest. Narrowing this to `{ vitals: ... }` (the old shape) discarded
    //   fields the core already produced and hid the EndSession record from
    //   downstream tooling.
    //   prevVitals = the same full message for each prior attempt, newest
    //   first; the API accepts both this and the legacy per-signal shape.
    const result = {
      vitals: wasmResultsAllAttempts[0],
      signsMsgs: signsMessagesAllAttempts[0], // All collected signs messages
      prevVitals: wasmResultsAllAttempts.slice(1),
      prevSignsMsgs: signsMessagesAllAttempts.slice(1),
      finalVitalsMeasurementValues, // Required for score display
      noValidMeasurements: options.noValidMeasurements ?? false,
      endReason: options.endReason ?? null, // null for normal end; e.g. 'face-lost' on the give-up path
      webAppVersion: '1.0.0',
      frameCollectionMethod: 'web-sdk',
      vitalCoreVersion: coreVersion ?? null, // From the core's getVersionString; cached at worker-ready, fetched at stop if needed
      metadata: metadataAllAttempts[0],
      prevMetadata: metadataAllAttempts.slice(1),
      timestamp,
      captureTimeUsed,
      rawVideoDimensions,
      coreVideoDimensions,
    };

    const resultData ={
      result,
      sessionComplete,
      remainingSessionAttempts,
    };

    frameMode = 'idle';
    statusManager.updateStatus({ measuring: false });
    clearInterval(countdownSetIntervalId);
    emitter.emit('stop', resultData);
    emitter.emit('timeLeft', 0);

    return resultData;
  };

  /**
   * Ingest video frames for processing
   */
  const ingestFrames = (videoElement) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }

    videoEl = videoElement;

    const onVideoFrameCallback = async (_now, metadata) => {
      let timestamp;
      const t0 = performance.now();
      if ('captureTime' in metadata) {
        timestamp = Math.round(metadata.captureTime);
        if (!captureTimeUsed) captureTimeUsed = true;
      } else {
        timestamp = Date.now();
      }

      const imgBitmap = await createImageBitmap(videoEl, imageXOffset, imageYOffset, imageWidth, imageHeight);
      const dur = performance.now() - t0;
      bitmapCreateMs = dur;
      bitmapCreateSum += dur;
      if (dur > bitmapCreateMax) bitmapCreateMax = dur;

      framesSent++;
      bytesSent += videoEl.videoWidth * videoEl.videoHeight * 4;
      workerManager.detectVitalsJS(imgBitmap, imageWidth, imageHeight, timestamp);
      imgBitmap.close();

      videoFrameCallback = videoEl.requestVideoFrameCallback(onVideoFrameCallback);
    };

    videoFrameCallback = videoEl.requestVideoFrameCallback(onVideoFrameCallback);
  }

  function startCountdownLoop() {
    currentTimeLeft = maxSessionTime;
    emitter.emit('timeLeft', {
      timeLeft: currentTimeLeft,
      maxTime: maxSessionTime,
      percentComplete: 0,
      percentRemaining: 1,
    });

    countdownSetIntervalId = setInterval(() => {
      currentTimeLeft--;

      const percentRemaining = currentTimeLeft / maxSessionTime;
      const percentComplete = 1 - percentRemaining;

      emitter.emit('timeLeft', {
        timeLeft: currentTimeLeft,
        maxTime: maxSessionTime,
        percentComplete,
        percentRemaining,
      });

      if (currentTimeLeft <= 0) {
        clearInterval(countdownSetIntervalId);
      }
    }, 1000);
  }

  /**
   * Get version string from worker
   */
  const getVersion = async () => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }
    if (coreVersion != null) {
      return coreVersion;
    }
    coreVersion = await workerManager.callMethod('getVersionString');
    return coreVersion;
  };

  /**
   * Set face guide region
   */
  const setFaceGuide = async (faceGuide) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }
    return await workerManager.callMethod('setFaceGuide', faceGuide);
  };

  /**
   * Enable/disable extended logging
   */
  const setExtendedLogs = async (enabled) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }
    return await workerManager.callMethod('setExtendedLogs', enabled);
  };

  /**
   * Update SDK options
   */
  const updateOptions = (patch = {}) => {
    Object.assign(clientConfig, patch);
    // TODO: Notify worker of config changes
  };

  /**
   * Get frame counts (sent to worker vs processed by worker)
   */
  const getFrameCounts = () => {
    const bitmapCreateAvg = framesSent > 0 ? bitmapCreateSum / framesSent : 0;
    return { framesSent, framesProcessed, bytesSent, frameMode, sessionCount, bitmapCreateMs: Math.round(bitmapCreateMs * 100) / 100, bitmapCreateMax: Math.round(bitmapCreateMax * 100) / 100, bitmapCreateAvg: Math.round(bitmapCreateAvg * 100) / 100 };
  };

  /**
   * Get current status
   */
  const getStatus = () => {
    return statusManager.getStatus();
  };

  /**
   * Register log handler
   */
  const onLog = (handler) => {
    if (workerManager) {
      workerManager.onLog(handler);
    }
  };

  /**
   * Unregister log handler
   */
  const offLog = () => {
    if (workerManager) {
      workerManager.offLog();
    }
  };

  // True only when every enabled vital is at percentDone>=1. Iterates the enabled
  // set (not the frame's signs) so a missing or stalled vital counts as not-done
  // and the session falls back to the max_session_time ceiling.
  function allEnabledVitalsComplete(signs) {
    const keys = authorizedVitals;
    if (keys.length === 0) return false;
    for (const key of keys) {
      const v = signs[key];
      if (!v || !(v.percentDone >= 0.999)) return false;
    }
    return true;
  }

  /**
   * Auth vital core
   */
  const authorize = async () => {
    if (!vitalsHaveBeenAuthorized) {
      try {
        if (!clientConfig.authenticator) {
          throw new Error('Authenticator callback not provided in config');
        }

        let authorizeVitalsResp;
        try {
          authorizeVitalsResp = await workerManager.authorizeVitalCore(
            clientConfig.authenticator
          );
        } catch {
          throw new Error('Unable to verify the license key to use Vital-Trac.');
        }

        let vitalsToMeasure = [];
        maxSessionTime = 0;

        if (authorizeVitalsResp.status === 'authorized') {
          if (!authorizeVitalsResp.vitals) {
            throw new Error('The user or clinic is unlicensed. Please contact an administrator to enable access to Vital-Trac.');
          } else {
            vitalsToMeasure = authorizeVitalsResp.vitals.reduce((vitalsToMeasure, vital) => {
              vitalsToMeasure.push(vital.tag);
              maxSessionTime = Math.max(vital.max_session_time, maxSessionTime);

              return vitalsToMeasure;
            }, []);
          }
        } else {
          throw new Error('There is not an active license key to use Vital-Trac.');
        }

        emitter.emit('authorizedVitals', vitalsToMeasure);
        emitter.emit('maxSessionTime', maxSessionTime);

        authorizedVitals = vitalsToMeasure;
        vitalsHaveBeenAuthorized = true;
      } catch (e) {
        console.log('error calling workingManager.authorizeVitalCore');
        throw e;
      }
    } else {
      return { authorizedVitals, maxSessionTime }
    }
  }

  /**
   * Begin a fresh measurement session, discarding the previous session's
   * attempt budget and accumulated results.
   *
   * A session normally ends when every vital has a value (or the attempts run
   * out), which leaves remainingSessionAttempts at 0 and makes any further
   * start() throw. That is the right default for a single measurement, but an
   * application that deliberately measures the same person several times in a
   * row, for example a study protocol that always takes a fixed number of
   * scans, whether or not each one succeeds, needs to reset between them.
   *
   * Only per-session state is cleared. Authorization, the worker and the loaded
   * models are left intact, so this is cheap and does not require re-running
   * init() or authorize().
   */
  const startNewSession = () => {
    if (frameMode === 'session') {
      throw new Error('Cannot start a new session while a measurement is running');
    }

    remainingSessionAttempts = 3;
    vitalsSessionData = {};
    finalVitalsMeasurementValues = {};
    wasmResultsAllAttempts = [];
    signsMessagesAllAttempts = [];
    metadataAllAttempts = [];
    currentAttemptSignsMsgs = {};
    captureTimeUsed = false;
    restarting = false;
    faceLostRestartCount = 0;
    currentSelectedVitals = null;
    currentSessionTags = null;

    emitter.emit('newSession');
  };

  /**
   * Cleanup and destroy client
   */
  const destroy = () => {
    if (workerManager) {
      workerManager.destroy();
    }
    emitter.emit('destroyed');
    emitter.removeAllListeners();
    initialized = false;
    statusManager.reset();
  };

  return {
    // Core methods
    init,
    start,
    stop,
    startNewSession,
    destroy,
    startPreviewMode,
    stopPreviewMode,

    // Worker methods
    getVersion,
    authorize,
    setExtendedLogs,

    // Logging
    onLog,
    offLog,

    // Configuration
    updateOptions,
    getStatus,
    getFrameCounts,

    // Event emitter methods
    on: emitter.on.bind(emitter),
    once: emitter.once.bind(emitter),
    off: emitter.off.bind(emitter),

    // Expose for testing and advanced usage
    get initialized() { return initialized; },
  };
}
